import { paletteCss } from "../scene/skeletonGroup";
import type { Poses } from "../data/types";

/**
 * Per-global-id checkbox panel. Lists every gid that appears in the loaded
 * poses bundle, each prefixed with its palette color swatch. Unchecking a
 * gid adds it to the supplied `disabledGids` set (the SkeletonGroups reads
 * that set every frame, so the change is live).
 *
 * Also offers "all / none" buttons for quick reset.
 */
export interface GidPicker {
  /** Force-rebuild the swatch list (e.g. after loading a new bundle). */
  refresh(): void;
}

export function setupGidPicker(
  poses: Poses,
  disabledGids: Set<number>,
): GidPicker {
  const root = document.getElementById("gid-picker") as HTMLDivElement;
  if (!root) throw new Error("missing #gid-picker element");

  // Collect the set of gids that ever appear, plus their frame counts so
  // the user can see which players are major vs. fleeting outliers.
  const counts = new Map<number, number>();
  for (const f of poses.frames) {
    for (const p of f.players) {
      counts.set(p.gid, (counts.get(p.gid) ?? 0) + 1);
    }
  }
  const gids = Array.from(counts.keys()).sort((a, b) => a - b);

  function render(): void {
    root.innerHTML = "";

    const header = document.createElement("div");
    header.className = "gid-picker-header";
    const title = document.createElement("span");
    title.textContent = `players (${gids.length})`;
    title.className = "gid-picker-title";
    const allBtn = document.createElement("button");
    allBtn.textContent = "all";
    allBtn.title = "Enable every player";
    allBtn.addEventListener("click", () => {
      disabledGids.clear();
      render();
    });
    const noneBtn = document.createElement("button");
    noneBtn.textContent = "none";
    noneBtn.title = "Disable every player";
    noneBtn.addEventListener("click", () => {
      for (const g of gids) disabledGids.add(g);
      render();
    });
    header.append(title, allBtn, noneBtn);
    root.appendChild(header);

    const list = document.createElement("div");
    list.className = "gid-list";
    for (const gid of gids) {
      const row = document.createElement("label");
      row.className = "gid-row";

      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = !disabledGids.has(gid);
      cb.addEventListener("change", () => {
        if (cb.checked) disabledGids.delete(gid);
        else disabledGids.add(gid);
      });

      const sw = document.createElement("span");
      sw.className = "gid-swatch";
      sw.style.background = paletteCss(gid);

      const lab = document.createElement("span");
      lab.className = "gid-label";
      const n = counts.get(gid) ?? 0;
      lab.textContent = `P${gid} (${n}f)`;

      row.append(cb, sw, lab);
      list.appendChild(row);
    }
    root.appendChild(list);
  }

  render();
  return { refresh: render };
}
